import { useEffect, useState } from 'react';
import { createCheckoutSession, createPortalSession, getCurrentUser } from '../api/client';
import { formatAppError } from '../utils/errorMessage';

export default function SubscriptionPlans() {
  const isAuthenticated = Boolean(localStorage.getItem('token'));
  const [submittingPlan, setSubmittingPlan] = useState<string | null>(null);
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [error, setError] = useState('');
  const [planLoadError, setPlanLoadError] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<'free' | 'starter' | 'pro' | 'scale' | 'enterprise'>('free');
  const [planStatus, setPlanStatus] = useState<string | null>(null);
  const [periodStart, setPeriodStart] = useState<string | null>(null);
  const [periodEnd, setPeriodEnd] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setCurrentPlan('free');
      return;
    }

    const loadProfile = async () => {
      try {
        const response = await getCurrentUser();
        const planName = response.data?.plan_name;
        setPlanStatus(response.data?.plan_status ?? null);
        setPeriodStart(response.data?.subscription_period_start ?? null);
        setPeriodEnd(response.data?.subscription_period_end ?? null);
        setCancelAtPeriodEnd(response.data?.cancel_at_period_end ?? null);
        if (planName === 'starter' || planName === 'pro' || planName === 'scale' || planName === 'enterprise') {
          setCurrentPlan(planName);
        } else {
          setCurrentPlan('free');
        }
      } catch (err) {
        setCurrentPlan('free');
        setPlanStatus(null);
        setPeriodStart(null);
        setPeriodEnd(null);
        setCancelAtPeriodEnd(null);
        setPlanLoadError(true);
      }
    };

    loadProfile();
  }, [isAuthenticated]);

  const handleSubscribe = async (plan: 'starter' | 'pro' | 'scale') => {
    try {
      setSubmittingPlan(plan);
      setError('');
      const response = await createCheckoutSession(plan);
      const url = response.data?.checkout_url;
      if (url) {
        window.location.href = url;
        return;
      }
      setError('Unable to start checkout. Please try again.');
    } catch (err: any) {
      const formatted = formatAppError(err, 'billing-checkout', 'Unable to start checkout. Please try again.');
      setError(formatted.message);
    } finally {
      setSubmittingPlan(null);
    }
  };

  const handleManageSubscription = async () => {
    if (managingSubscription) return;
    try {
      setError('');
      setManagingSubscription(true);
      const response = await createPortalSession();
      const url = response.data?.portal_url;
      if (url) {
        window.location.href = url;
        return;
      }
      setError('Unable to open billing portal. Please try again.');
    } catch (err) {
      setError('Unable to open billing portal. Please try again.');
    } finally {
      setManagingSubscription(false);
    }
  };

  const renderCta = (plan: 'starter' | 'pro' | 'scale' | 'enterprise' | 'free') => {
    if (!isAuthenticated) {
      return (
        <a
          href="/register"
          className="mt-4 inline-flex items-center justify-center w-full px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors duration-200"
        >
          Sign up
        </a>
      );
    }

    if (plan === 'enterprise') {
      return (
        <a
          href="mailto:support@emotionwise.ai"
          className="mt-4 inline-flex items-center justify-center w-full px-4 py-2 text-sm font-semibold rounded-lg bg-slate-200 text-slate-900 hover:bg-slate-300 transition-colors duration-200"
        >
          Contact Sales
        </a>
      );
    }

    if (plan === 'free') {
      if (currentPlan !== 'free') {
        return (
          <button
            type="button"
            className="mt-4 inline-flex items-center justify-center w-full px-4 py-2 text-sm font-semibold rounded-lg bg-slate-800 text-slate-500 cursor-not-allowed"
            disabled
          >
            Sandbox Plan
          </button>
        );
      }

      return (
        <button
          type="button"
          disabled
          className="mt-4 inline-flex items-center justify-center w-full px-4 py-2 text-sm font-semibold rounded-lg bg-slate-700/80 text-slate-300 cursor-default"
        >
          Current Plan
        </button>
      );
    }

    if (plan === currentPlan) {
      return (
        <button
          type="button"
          disabled
          className="mt-4 inline-flex items-center justify-center w-full px-4 py-2 text-sm font-semibold rounded-lg bg-slate-700/80 text-slate-300 cursor-default"
        >
          Current Plan
        </button>
      );
    }

    if (currentPlan !== 'free') {
      return (
        <button
          type="button"
          onClick={handleManageSubscription}
          disabled={managingSubscription}
          className="mt-4 inline-flex items-center justify-center w-full px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          {managingSubscription ? 'Opening portal…' : 'Manage subscription'}
        </button>
      );
    }

    return (
      <button
        type="button"
        onClick={() => handleSubscribe(plan)}
        disabled={submittingPlan === plan}
        className="mt-4 inline-flex items-center justify-center w-full px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
      >
        {submittingPlan === plan ? 'Redirecting…' : 'Get started'}
      </button>
    );
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
      <div className="max-w-6xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-100 mb-3">
            Developer Plans for EmotionWise API
          </h1>
          <p className="text-lg text-slate-300 max-w-3xl mx-auto">
            Start in sandbox, then move to production with quota-based plans built for real API traffic.
          </p>
        </div>

        {planLoadError && (
          <div className="mb-4 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-sm text-amber-300 text-center">
            Could not load your current plan. Showing defaults — refresh to try again.
          </div>
        )}
        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-sm text-red-300 text-center">
            {error}
          </div>
        )}

        {isAuthenticated && (
          <div className="mb-8 rounded-2xl border border-indigo-500/30 bg-slate-900/80 p-6 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-indigo-300 uppercase tracking-wide">Current Plan & Billing Window</p>
                <h2 className="text-2xl font-bold text-slate-100 mt-1">
                  {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}
                </h2>
                {planStatus && (
                  <p className="text-sm text-slate-300 mt-1">Status: {planStatus}</p>
                )}
                {cancelAtPeriodEnd && periodEnd && (
                  <p className="mt-2 inline-flex items-center rounded-full bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-300 border border-amber-500/30">
                    Cancels at period end on {new Date(periodEnd).toLocaleDateString()}
                  </p>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-4 text-sm text-slate-300">
                <div className="rounded-xl bg-slate-800/80 px-4 py-3">
                  <div className="text-xs uppercase text-slate-400">Started</div>
                  <div className="font-semibold text-slate-100">
                    {periodStart ? new Date(periodStart).toLocaleDateString() : '—'}
                  </div>
                </div>
                <div className="rounded-xl bg-slate-800/80 px-4 py-3">
                  <div className="text-xs uppercase text-slate-400">Renews / Ends</div>
                  <div className="font-semibold text-slate-100">
                    {periodEnd ? new Date(periodEnd).toLocaleDateString() : '—'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleManageSubscription}
                  disabled={managingSubscription}
                  className="rounded-xl bg-indigo-600 px-4 py-3 text-white font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {managingSubscription ? 'Opening portal…' : 'Manage subscription'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5">
          {/* Free */}
          <div className="bg-slate-900/80 border border-slate-700 rounded-2xl p-6 shadow-sm h-full flex flex-col">
            {currentPlan === 'free' && (
              <span className="inline-flex items-center self-start px-3 py-1 text-xs font-semibold uppercase tracking-wide rounded-full border border-cyan-400/30 bg-cyan-500/10 text-cyan-200">
                Current Plan
              </span>
            )}
            <h2 className="text-xl font-semibold text-slate-100">Free</h2>
            <p className="text-sm text-slate-400 mt-1">Best for sandbox testing</p>
            <div className="mt-4 text-3xl font-bold text-slate-100">Free</div>
            <ul className="mt-4 space-y-2 text-sm text-slate-300 flex-1">
              <li>1,000 API calls / month</li>
              <li>Emotion + sarcasm detection</li>
              <li>Community support</li>
              <li>Account required</li>
            </ul>
            <div className="mt-6">{renderCta('free')}</div>
          </div>

          {/* Starter */}
          <div className="bg-slate-900/80 border border-cyan-500/40 rounded-2xl p-6 shadow-sm h-full flex flex-col">
            {currentPlan === 'starter' && (
              <span className="inline-flex items-center self-start px-3 py-1 text-xs font-semibold uppercase tracking-wide rounded-full border border-cyan-400/30 bg-cyan-500/10 text-cyan-200">
                Current Plan
              </span>
            )}
            <h2 className="text-xl font-semibold text-slate-100">Starter</h2>
            <p className="text-sm text-slate-400 mt-1">For early production workloads</p>
            <div className="mt-4 text-3xl font-bold text-slate-100">$9.99<span className="text-base text-slate-400">/mo</span></div>
            <ul className="mt-4 space-y-2 text-sm text-slate-300 flex-1">
              <li>10,000 API calls / month</li>
              <li>Same API response contract</li>
              <li>Email support</li>
            </ul>
            <div className="mt-6">{renderCta('starter')}</div>
          </div>

          {/* Pro */}
          <div className="bg-slate-900/80 border-2 border-indigo-500/70 rounded-2xl p-6 shadow-lg shadow-indigo-500/10 h-full flex flex-col relative">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center px-3 py-1 text-xs font-bold uppercase tracking-wide rounded-full bg-indigo-600 text-white shadow-sm">
              Most Popular
            </span>
            {currentPlan === 'pro' && (
              <span className="inline-flex items-center self-start px-3 py-1 text-xs font-semibold uppercase tracking-wide rounded-full border border-cyan-400/30 bg-cyan-500/10 text-cyan-200">
                Current Plan
              </span>
            )}
            <h2 className="text-xl font-semibold text-slate-100">Pro</h2>
            <p className="text-sm text-slate-400 mt-1">For production-ready startups and teams</p>
            <div className="mt-4 text-3xl font-bold text-slate-100">$29.99<span className="text-base text-slate-400">/mo</span></div>
            <ul className="mt-4 space-y-2 text-sm text-slate-300 flex-1">
              <li>25,000 API calls / month</li>
              <li>Higher rate limits</li>
              <li>Priority email support</li>
            </ul>
            <div className="mt-6">{renderCta('pro')}</div>
          </div>

          {/* Scale */}
          <div className="bg-slate-900/80 border border-purple-500/40 rounded-2xl p-6 shadow-sm h-full flex flex-col">
            {currentPlan === 'scale' && (
              <span className="inline-flex items-center self-start px-3 py-1 text-xs font-semibold uppercase tracking-wide rounded-full border border-cyan-400/30 bg-cyan-500/10 text-cyan-200">
                Current Plan
              </span>
            )}
            <h2 className="text-xl font-semibold text-slate-100">Scale</h2>
            <p className="text-sm text-slate-400 mt-1">For high-volume products</p>
            <div className="mt-4 text-3xl font-bold text-slate-100">$99<span className="text-base text-slate-400">/mo</span></div>
            <ul className="mt-4 space-y-2 text-sm text-slate-300 flex-1">
              <li>250,000 API calls / month</li>
              <li>Usage dashboards</li>
              <li>Early access to new models</li>
            </ul>
            <div className="mt-6">{renderCta('scale')}</div>
          </div>

          {/* Enterprise */}
          <div className="bg-slate-900/80 border border-slate-700 rounded-2xl p-6 shadow-sm h-full flex flex-col">
            {currentPlan === 'enterprise' && (
              <span className="inline-flex items-center self-start px-3 py-1 text-xs font-semibold uppercase tracking-wide rounded-full border border-cyan-400/30 bg-cyan-500/10 text-cyan-200">
                Current Plan
              </span>
            )}
            <h2 className="text-xl font-semibold text-slate-100">Enterprise</h2>
            <p className="text-sm text-slate-400 mt-1">Custom solutions</p>
            <div className="mt-4 text-3xl font-bold text-slate-100">Custom</div>
            <ul className="mt-4 space-y-2 text-sm text-slate-300 flex-1">
              <li>SLA & dedicated support</li>
              <li>Custom limits & onboarding</li>
              <li>On-prem or private deployments</li>
            </ul>
            <div className="mt-6">{renderCta('enterprise')}</div>
          </div>
        </div>

        <div className="mt-10 bg-slate-900/80 border border-slate-700 rounded-2xl p-6 text-center">
          <h3 className="text-xl font-semibold text-slate-100">Need a custom plan?</h3>
          <p className="text-sm text-slate-300 mt-2">
            Contact us at <a className="text-indigo-300 hover:text-indigo-200 underline" href="mailto:support@emotionwise.ai">support@emotionwise.ai</a>
          </p>
        </div>
      </div>
    </div>
  );
}
